import React from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface User {
  id: string;
  username: string;
  photo_url: string;
  bio: string;
}

interface FollowListModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  type: 'followers' | 'following';
  title: string;
}

const FollowListModal: React.FC<FollowListModalProps> = ({ isOpen, onClose, userId, type, title }) => {
  const [users, setUsers] = React.useState<User[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchUsers = async () => {
      if (!isOpen) return;
      
      setIsLoading(true);
      try {
        const { data: followData, error: followError } = await supabase
          .from('follows')
          .select(type === 'followers' ? 'follower_id' : 'following_id')
          .eq(type === 'followers' ? 'following_id' : 'follower_id', userId);

        if (followError) throw followError;

        // Ensure followData exists and has items before proceeding
        if (followData && followData.length > 0) {
          const userIds = followData.map(item => 
            type === 'followers' ? item.follower_id : item.following_id
          );

          const { data: profilesData, error: profilesError } = await supabase
            .from('profiles')
            .select('id, username, photo_url, bio')
            .in('id', userIds);

          if (profilesError) throw profilesError;
          setUsers(profilesData || []);
        } else {
          setUsers([]);
        }
      } catch (error) {
        console.error(`Error fetching ${type}:`, error);
        setUsers([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [userId, type, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-gray-900/90 backdrop-blur-xl rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        <div className="sticky top-0 bg-gray-900/90 backdrop-blur-xl p-6 border-b border-gray-800">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white">{title}</h2>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[60vh]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400">No {type} yet</p>
            </div>
          ) : (
            <div className="p-4">
              {users.map((user) => (
                <Link
                  key={user.id}
                  to={`/home/profile/${user.username.toLowerCase()}`}
                  onClick={onClose}
                  className="flex items-center space-x-4 p-4 rounded-lg hover:bg-gray-800/50 transition-colors"
                >
                  <img
                    src={user.photo_url}
                    alt={user.username}
                    className="w-12 h-12 rounded-full object-cover border border-gray-700"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium">{user.username.toLowerCase()}</h3>
                    {user.bio && (
                      <p className="text-gray-400 text-sm truncate">{user.bio}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowListModal;